import { ActionButton } from '@/components/common/ActionButton';
import { TOOL_MANAGEMENT_DIALOG_PT } from '@/components/dialogs/dialogTheme';
import {
  ArrowCounterClockwise,
  ArrowsIn,
  ArrowsOut,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
} from '@phosphor-icons/react';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ImagePreviewDialogProps {
  visible: boolean;
  title: string;
  imageUrl: string | null;
  error?: boolean;
  onClose: () => void;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.1;
const WHEEL_ZOOM_SENSITIVITY = 0.001;

export const ImagePreviewDialog: FC<ImagePreviewDialogProps> = ({
  visible,
  title,
  imageUrl,
  error = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const constrainPan = useCallback((newPanX: number, newPanY: number, currentZoom: number) => {
    if (!imageRef.current || !containerRef.current) {
      return { x: newPanX, y: newPanY };
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const imgRect = imageRef.current.getBoundingClientRect();

    // Image natural size; scaled size = natural * zoom (imgRect is already scaled by zoom)
    const imgWidth = imgRect.width / currentZoom;
    const imgHeight = imgRect.height / currentZoom;
    const scaledWidth = imgWidth * currentZoom;
    const scaledHeight = imgHeight * currentZoom;

    // Container dimensions (accounting for padding)
    const containerWidth = containerRect.width - 32; // 32px = 2 * 16px padding
    const containerHeight = containerRect.height - 32;

    // Pan bounds: image center is at (containerWidth/2 + pan.x, containerHeight/2 + pan.y).
    // Left edge = containerWidth/2 - scaledWidth/2 + pan.x; keep it in [0, containerWidth - scaledWidth].
    // So pan.x in [(containerWidth - scaledWidth)/2, (scaledWidth - containerWidth)/2] (minX <= maxX when zoomed).
    const minX = (containerWidth - scaledWidth) / 2;
    const maxX = (scaledWidth - containerWidth) / 2;
    const minY = (containerHeight - scaledHeight) / 2;
    const maxY = (scaledHeight - containerHeight) / 2;

    return {
      x: Math.max(minX, Math.min(maxX, newPanX)),
      y: Math.max(minY, Math.min(maxY, newPanY)),
    };
  }, []);

  // Reset zoom and pan when dialog opens/closes or image changes
  useEffect(() => {
    if (visible && imageUrl) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  }, [visible, imageUrl]);

  // Constrain pan when zoom changes
  useEffect(() => {
    if (zoom !== 1 && imageRef.current && containerRef.current) {
      setPan((currentPan) => constrainPan(currentPan.x, currentPan.y, zoom));
    }
  }, [zoom, constrainPan]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (visible) {
      // Store original overflow style
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [visible]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP * 5, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - ZOOM_STEP * 5, MIN_ZOOM));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!imageRef.current || !containerRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const delta = e.deltaY * WHEEL_ZOOM_SENSITIVITY;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom - delta));

    if (newZoom !== zoom) {
      // Get mouse position relative to container
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Container center
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Calculate zoom point relative to image center
      const zoomPointX = (mouseX - centerX - pan.x) / zoom;
      const zoomPointY = (mouseY - centerY - pan.y) / zoom;

      // Calculate new pan to keep zoom point under cursor
      const newPanX = mouseX - centerX - zoomPointX * newZoom;
      const newPanY = mouseY - centerY - zoomPointY * newZoom;

      // Constrain pan to boundaries
      const constrained = constrainPan(newPanX, newPanY, newZoom);

      setZoom(newZoom);
      setPan(constrained);
    }
  };

  // Prevent page scroll when wheel event happens anywhere in the dialog
  const handleDialogWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Only prevent if we're over the image container
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      if (mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom > 1 && e.button === 0) {
      setIsPanning(true);
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setPanStart({
          x: e.clientX - rect.left - pan.x,
          y: e.clientY - rect.top - pan.y,
        });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPanning && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newPanX = e.clientX - rect.left - panStart.x;
      const newPanY = e.clientY - rect.top - panStart.y;
      const constrained = constrainPan(newPanX, newPanY, zoom);
      setPan(constrained);
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleMouseLeave = () => {
    setIsPanning(false);
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const fullscreenLabel = isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen');

  const dialogHeader = (
    <div className="flex w-full min-w-0 items-center justify-between gap-4 pr-2">
      <span className="min-w-0 truncate text-lg font-semibold text-gray-900">{title}</span>
      <ActionButton
        variant="ghost"
        iconOnly={true}
        icon={isFullscreen ? <ArrowsIn size={16} aria-hidden="true" /> : <ArrowsOut size={16} aria-hidden="true" />}
        onClick={handleToggleFullscreen}
        title={fullscreenLabel}
        aria-label={fullscreenLabel}
        className="shrink-0"
      />
    </div>
  );

  const dialogFooter = (
    <ActionButton variant="ghost" onClick={onClose}>
      {t('common.close')}
    </ActionButton>
  );

  return (
    <Dialog
      header={dialogHeader}
      footer={dialogFooter}
      visible={visible}
      onHide={onClose}
      modal={true}
      className={`w-full ${isFullscreen ? 'max-w-full h-full' : 'max-w-4xl'}`}
      dismissableMask={true}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        ...TOOL_MANAGEMENT_DIALOG_PT,
        root: {
          className: isFullscreen
            ? 'border border-gray-200 rounded-2xl shadow-lg fixed inset-0 h-full m-0'
            : TOOL_MANAGEMENT_DIALOG_PT.root.className,
        },
        content: {
          className: `${TOOL_MANAGEMENT_DIALOG_PT.content.className} ${isFullscreen ? 'h-full flex flex-col' : ''}`,
        },
      }}
    >
      <div className={`flex flex-col gap-4 ${isFullscreen ? 'h-full' : ''}`} onWheel={handleDialogWheel}>
        {error || !imageUrl ? (
          <div className="flex items-center justify-center rounded-xl bg-gray-50 px-4 py-12">
            <p className="m-0 text-sm text-gray-500">{t('dialogs.previewUnavailable')}</p>
          </div>
        ) : (
          <div className="relative">
            {/* Zoom controls */}
            <div className="absolute top-2 right-2 z-10 flex gap-1 rounded-lg border border-gray-200 bg-white/90 p-1 shadow-sm backdrop-blur-sm">
              <ActionButton
                variant="ghost"
                iconOnly={true}
                icon={<MagnifyingGlassMinus size={16} aria-hidden="true" />}
                onClick={handleZoomOut}
                disabled={zoom <= MIN_ZOOM}
                title={t('dialogs.zoomOut')}
                aria-label={t('dialogs.zoomOut')}
              />
              <ActionButton
                variant="ghost"
                iconOnly={true}
                icon={<MagnifyingGlassPlus size={16} aria-hidden="true" />}
                onClick={handleZoomIn}
                disabled={zoom >= MAX_ZOOM}
                title={t('dialogs.zoomIn')}
                aria-label={t('dialogs.zoomIn')}
              />
              <ActionButton
                variant="ghost"
                iconOnly={true}
                icon={<ArrowCounterClockwise size={16} aria-hidden="true" />}
                onClick={handleResetZoom}
                disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                title={t('dialogs.resetZoom')}
                aria-label={t('dialogs.resetZoom')}
              />
            </div>

            {/* Image container with zoom and pan */}
            <div
              ref={containerRef}
              className={`flex cursor-move items-center justify-center overflow-hidden rounded-xl bg-gray-50 p-4 ${isFullscreen ? 'flex-1' : ''}`}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              style={{ cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
            >
              <div
                className="flex items-center justify-center transition-transform duration-100"
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                }}
              >
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt={title}
                  className={`max-w-full select-none object-contain ${isFullscreen ? 'max-h-[calc(100vh-200px)]' : 'max-h-[70vh]'}`}
                  draggable={false}
                />
              </div>
            </div>

            {/* Zoom level indicator */}
            {zoom !== 1 && (
              <div className="absolute bottom-2 left-2 rounded-full border border-gray-200 bg-white/90 px-3 py-1 text-xs font-medium tabular-nums text-gray-700 backdrop-blur-sm">
                {Math.round(zoom * 100)}%
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
